// WDC Global Command Center - Main Dashboard Component
// React component for the unified hub linking all products

import React, { useState, useEffect } from 'react';
import { 
  ProductId, User, AIWorker, ProfitLoop, 
  SystemConfig 
} from '../types';
import { PRODUCTS } from '../lib/billing';
import { aiWorkerEngine, AIWorkerEngine } from '../lib/workers';
import { getSystemConfig, hasPermission } from '../lib/auth';

interface CommandCenterProps {
  user: User;
}

const productOrder: ProductId[] = ['command-center', 'agentguard', 'complianceos', 'permitflow', 'regwatch'];

const productIcons: Record<ProductId, React.ReactNode> = {
  'command-center': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>,
  'agentguard': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  'complianceos': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>,
  'permitflow': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  'regwatch': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
};

export const CommandCenter: React.FC<CommandCenterProps> = ({ user }) => {
  const [activeProduct, setActiveProduct] = useState<ProductId>('command-center');
  const [workers, setWorkers] = useState<AIWorker[]>([]);
  const [profitLoops, setProfitLoops] = useState<ProfitLoop[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<{ totalRevenue: number; totalCost: number; netProfit: number; roi: number; activeLoops: number; activeWorkers: number }>({
    totalRevenue: 0, totalCost: 0, netProfit: 0, roi: 0, activeLoops: 0, activeWorkers: 0
  });
  const [systemConfig] = useState<SystemConfig>(getSystemConfig());
  const [showWorkerModal, setShowWorkerModal] = useState<AIWorker | null>(null);
  const [showLoopModal, setShowLoopModal] = useState<ProfitLoop | null>(null);

  useEffect(() => {
    // Initialize workers and profit loops
    const engine = aiWorkerEngine as AIWorkerEngine;
    engine.createDefaultWorkers();
    engine.createDefaultProfitLoops();
    
    const refreshData = () => {
      setWorkers(engine.getAllWorkers());
      setProfitLoops(engine.getAllProfitLoops());
      setSystemMetrics(engine.getSystemProfitMetrics());
    };

    refreshData();
    const interval = setInterval(refreshData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const currentProduct = PRODUCTS[activeProduct];
  const productWorkers = workers.filter(w => w.productId === activeProduct);
  const productLoops = profitLoops.filter(l => {
    const worker = workers.find(w => w.id === l.workerId);
    return worker?.productId === activeProduct;
  });

  const getProductStatus = (productId: ProductId): 'active' | 'inactive' | 'locked' => {
    const product = PRODUCTS[productId];
    if (!product.enabled) return 'inactive';
    if (!hasPermission(user, `product:${productId}`, 'read')) return 'locked';
    return activeProduct === productId ? 'active' : 'inactive';
  };

  return (
    <div className="command-center" style={styles.container}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.logo}>
          <svg style={styles.logoIcon} viewBox="0 0 100 100">
            <defs>
              <linearGradient id="masterGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00A6FF" />
                <stop offset="100%" stopColor="#0A2540" />
              </linearGradient>
            </defs>
            <path d="M50 10 L90 30 V70 L50 90 L10 70 V30 Z" fill="none" stroke="url(#masterGrad)" strokeWidth="3" />
            <path d="M30 70 L40 30 L50 55 L60 30 L70 70" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={styles.logoText}>WDC Global</span>
        </div>

        <nav style={styles.nav}>
          {productOrder.map(productId => {
            const product = PRODUCTS[productId];
            const status = getProductStatus(productId);
            return (
              <button
                key={productId}
                onClick={() => setActiveProduct(productId)}
                disabled={status === 'locked'}
                style={{
                  ...styles.navItem,
                  ...(status === 'active' ? styles.navItemActive : {}),
                  ...(status === 'locked' ? styles.navItemLocked : {}),
                  borderLeftColor: product.color,
                }}
              >
                <span style={{ ...styles.navIcon, color: product.color }}>
                  {productIcons[productId]}
                </span>
                <span style={styles.navLabel}>{product.name}</span>
                {status === 'locked' && <span style={styles.lockBadge}>🔒</span>}
                {status === 'active' && <span style={styles.activeDot}></span>}
              </button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div style={styles.userProfile}>
          <div style={styles.userAvatar}>{user.name.charAt(0)}</div>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{user.name}</div>
            <div style={styles.userRole}>{user.role.replace('_', ' ')}</div>
          </div>
          {user.role === 'head_admin' && <span style={styles.adminBadge}>HEAD ADMIN</span>}
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.main}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.pageTitle}>{currentProduct.name}</h1>
            <p style={styles.pageDesc}>{currentProduct.description}</p>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.metricCard}>
              <div style={styles.metricValue}>${systemMetrics.totalRevenue.toLocaleString()}</div>
              <div style={styles.metricLabel}>Total Revenue</div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricValue}>${systemMetrics.netProfit.toLocaleString()}</div>
              <div style={styles.metricLabel}>Net Profit</div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricValue}>{systemMetrics.roi.toFixed(1)}%</div>
              <div style={styles.metricLabel}>ROI</div>
            </div>
          </div>
        </header>

        {/* Product Grid / Dashboard */}
        <section style={styles.content}>
          {/* Product Feature Cards */}
          <div style={styles.featureGrid}>
            {currentProduct.features.map((feature, i) => (
              <div key={i} style={styles.featureCard}>
                <span style={styles.featureIcon}>✦</span>
                <span style={styles.featureText}>{feature}</span>
              </div>
            ))}
          </div>

          {/* Workers Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>AI Workers</h2>
              <button onClick={() => setShowWorkerModal({} as AIWorker)} style={styles.addButton}>+ New Worker</button>
            </div>
            <div style={styles.workerGrid}>
              {productWorkers.map(worker => (
                <WorkerCard 
                  key={worker.id} 
                  worker={worker} 
                  onClick={() => setShowWorkerModal(worker)}
                  onExecute={() => aiWorkerEngine.executeWorker(worker.id)}
                />
              ))}
              {productWorkers.length === 0 && (
                <div style={styles.emptyState}>No workers for this product yet</div>
              )}
            </div>
          </div>

          {/* Profit Loops Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Profit Loops</h2>
              <button onClick={() => setShowLoopModal({} as ProfitLoop)} style={styles.addButton}>+ New Loop</button>
            </div>
            <div style={styles.loopGrid}>
              {productLoops.map(loop => (
                <ProfitLoopCard key={loop.id} loop={loop} onClick={() => setShowLoopModal(loop)} />
              ))}
              {productLoops.length === 0 && (
                <div style={styles.emptyState}>No profit loops for this product yet</div>
              )}
            </div>
          </div>

          {/* System Features */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>System Features</h2>
            <div style={styles.featureFlags}>
              {Object.entries(systemConfig.features).map(([key, value]) => (
                <div key={key} style={styles.flagItem}>
                  <span style={{ ...styles.flagDot, backgroundColor: value ? '#22c55e' : '#ef4444' }}></span>
                  <span style={styles.flagLabel}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                  <span style={styles.flagStatus}>{value ? 'Enabled' : 'Disabled'}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Modals */}
      {showWorkerModal && (
        <WorkerModal worker={showWorkerModal} onClose={() => setShowWorkerModal(null)} />
      )}
      {showLoopModal && (
        <ProfitLoopModal loop={showLoopModal} onClose={() => setShowLoopModal(null)} />
      )}
    </div>
  );
};

// Worker Card Component
const WorkerCard: React.FC<{ worker: AIWorker; onClick: () => void; onExecute: () => void }> = ({ worker, onClick, onExecute }) => {
  const statusColors: Record<string, string> = {
    idle: '#64748b',
    running: '#3b82f6',
    paused: '#f59e0b',
    error: '#ef4444',
    completed: '#22c55e',
  };

  return (
    <div onClick={onClick} style={{ ...styles.card, borderLeftColor: statusColors[worker.status] }}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>{worker.name}</span>
        <span style={{ ...styles.statusBadge, backgroundColor: statusColors[worker.status] }}>{worker.status}</span>
      </div>
      <div style={styles.cardMeta}>
        <span>Type: {worker.type.replace('_', ' ')}</span>
        <span>Runs: {worker.metrics.runsTotal}</span>
        <span>Success: {worker.metrics.runsSuccessful}</span>
      </div>
      <div style={styles.cardMetrics}>
        <div><span style={styles.metricLabel}>Revenue:</span> <span>${worker.metrics.totalRevenueUsd.toFixed(2)}</span></div>
        <div><span style={styles.metricLabel}>Cost:</span> <span>${worker.metrics.totalCostUsd.toFixed(2)}</span></div>
        <div><span style={styles.metricLabel}>Profit:</span> <span style={{ color: worker.metrics.netProfitUsd >= 0 ? '#22c55e' : '#ef4444' }}>${worker.metrics.netProfitUsd.toFixed(2)}</span></div>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onExecute(); }} style={styles.executeBtn} disabled={worker.status === 'running'}>
        {worker.status === 'running' ? 'Running...' : 'Execute Now'}
      </button>
    </div>
  );
};

// Profit Loop Card Component
const ProfitLoopCard: React.FC<{ loop: ProfitLoop; onClick: () => void }> = ({ loop, onClick }) => {
  const statusColors: Record<string, string> = {
    active: '#22c55e',
    paused: '#f59e0b',
    testing: '#3b82f6',
    archived: '#64748b',
  };

  return (
    <div onClick={onClick} style={{ ...styles.card, borderLeftColor: statusColors[loop.status] }}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>{loop.name}</span>
        <span style={{ ...styles.statusBadge, backgroundColor: statusColors[loop.status] }}>{loop.status}</span>
      </div>
      <p style={styles.cardDesc}>{loop.description}</p>
      <div style={styles.cardMeta}>
        <span>Type: {loop.type.replace('_', ' ')}</span>
        <span>Runs: {loop.metrics.runsCount}</span>
      </div>
      <div style={styles.cardMetrics}>
        <div><span style={styles.metricLabel}>Revenue:</span> <span>${loop.metrics.totalRevenueUsd.toFixed(2)}</span></div>
        <div><span style={styles.metricLabel}>Cost:</span> <span>${loop.metrics.totalCostUsd.toFixed(2)}</span></div>
        <div><span style={styles.metricLabel}>ROI:</span> <span style={{ color: loop.metrics.roiPercent >= 0 ? '#22c55e' : '#ef4444' }}>{loop.metrics.roiPercent.toFixed(1)}%</span></div>
      </div>
    </div>
  );
};

// Worker Modal
const WorkerModal: React.FC<{ worker: AIWorker; onClose: () => void }> = ({ worker, onClose }) => {
  if (!worker.id) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3>{worker.name}</h3>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>
        <div style={styles.modalBody}>
          <p><strong>Type:</strong> {worker.type}</p>
          <p><strong>Product:</strong> {worker.productId}</p>
          <p><strong>Model:</strong> {worker.config.model}</p>
          <p><strong>Schedule:</strong> {worker.schedule.expression}</p>
          <p><strong>Status:</strong> {worker.status}</p>
          <div style={styles.modalMetrics}>
            <h4>Metrics</h4>
            <p>Total Runs: {worker.metrics.runsTotal}</p>
            <p>Successful: {worker.metrics.runsSuccessful}</p>
            <p>Failed: {worker.metrics.runsFailed}</p>
            <p>Total Revenue: ${worker.metrics.totalRevenueUsd.toFixed(2)}</p>
            <p>Total Cost: ${worker.metrics.totalCostUsd.toFixed(2)}</p>
            <p>Net Profit: ${worker.metrics.netProfitUsd.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Profit Loop Modal
const ProfitLoopModal: React.FC<{ loop: ProfitLoop; onClose: () => void }> = ({ loop, onClose }) => {
  if (!loop.id) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3>{loop.name}</h3>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>
        <div style={styles.modalBody}>
          <p><strong>Type:</strong> {loop.type}</p>
          <p><strong>Worker ID:</strong> {loop.workerId}</p>
          <p><strong>Description:</strong> {loop.description}</p>
          <p><strong>Status:</strong> {loop.status}</p>
          <div style={styles.modalMetrics}>
            <h4>Configuration</h4>
            <p>Target Revenue: ${loop.config.targetRevenueUsd.toLocaleString()}</p>
            <p>Max Cost: ${loop.config.maxCostUsd.toLocaleString()}</p>
            <p>Min Margin: {loop.config.minMarginPercent}%</p>
            <p>Reinvest: {loop.config.reinvestPercent}%</p>
          </div>
          <div style={styles.modalMetrics}>
            <h4>Metrics</h4>
            <p>Total Revenue: ${loop.metrics.totalRevenueUsd.toFixed(2)}</p>
            <p>Total Cost: ${loop.metrics.totalCostUsd.toFixed(2)}</p>
            <p>Net Profit: ${loop.metrics.netProfitUsd.toFixed(2)}</p>
            <p>ROI: {loop.metrics.roiPercent.toFixed(1)}%</p>
            <p>Runs: {loop.metrics.runsCount}</p>
            <p>Avg Revenue/Run: ${loop.metrics.avgRevenuePerRun.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const styles: any = {
  container: {
    display: 'flex',
    height: '100vh',
    background: '#0a0f1e',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#e2e8f0',
  },
  sidebar: {
    width: '280px',
    background: 'linear-gradient(180deg, #0f1729 0%, #1a2332 100%)',
    borderRight: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '40px',
    padding: '10px',
  },
  logoIcon: { width: '40px', height: '40px' },
  logoText: { fontSize: '20px', fontWeight: 900, letterSpacing: '-0.5px', background: 'linear-gradient(135deg, #00A6FF, #4DC9FF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  nav: { flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '12px',
    borderLeftWidth: '3px',
    color: '#e2e8f0',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
  },
  navItemActive: {
    background: 'rgba(0,166,255,0.1)',
    borderColor: 'rgba(0,166,255,0.3)',
    boxShadow: '0 0 20px rgba(0,166,255,0.1)',
  },
  navItemLocked: { opacity: 0.5, cursor: 'not-allowed' },
  navIcon: { width: '20px', height: '20px', flexShrink: 0 },
  navLabel: { flex: 1 },
  lockBadge: { fontSize: '12px' },
  activeDot: { width: '8px', height: '8px', background: '#00A6FF', borderRadius: '50%' },
  userProfile: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '12px',
    marginTop: '20px',
  },
  userAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #00A6FF, #0A2540)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '16px' },
  userInfo: { flex: 1 },
  userName: { fontWeight: 600, fontSize: '14px' },
  userRole: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', textTransform: 'capitalize' },
  adminBadge: { fontSize: '10px', fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '30px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
  },
  headerLeft: { flex: 1 },
  pageTitle: { fontSize: '28px', fontWeight: 900, margin: 0, letterSpacing: '-0.5px' },
  pageDesc: { color: 'rgba(255,255,255,0.6)', margin: '8px 0 0', fontSize: '14px' },
  headerRight: { display: 'flex', gap: '20px' },
  metricCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '12px',
    padding: '16px 24px',
    textAlign: 'center',
    minWidth: '140px',
  },
  metricValue: { fontSize: '20px', fontWeight: 900, color: '#00A6FF' },
  metricLabel: { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' },
  content: { padding: '30px 40px', display: 'flex', flexDirection: 'column', gap: '30px' },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  featureCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' },
  featureIcon: { color: '#00A6FF', fontSize: '18px' },
  featureText: { fontSize: '14px' },
  section: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  sectionTitle: { fontSize: '18px', fontWeight: 700, margin: 0 },
  addButton: { background: 'linear-gradient(135deg, #00A6FF, #0A2540)', border: 'none', borderRadius: '8px', padding: '10px 20px', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '13px' },
  workerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' },
  loopGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' },
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '20px', borderLeftWidth: '3px', cursor: 'pointer', transition: 'all 0.2s' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  cardTitle: { fontWeight: 600, fontSize: '14px' },
  statusBadge: { fontSize: '11px', padding: '2px 8px', borderRadius: '4px', color: '#fff', fontWeight: 600, textTransform: 'uppercase' },
  cardMeta: { display: 'flex', gap: '16px', fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' },
  cardDesc: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' },
  cardMetrics: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '12px' },
  executeBtn: { width: '100%', marginTop: '12px', padding: '8px', background: 'linear-gradient(135deg, #00A6FF, #0A2540)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 600, fontSize: '12px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' },
  featureFlags: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' },
  flagItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px' },
  flagDot: { width: '10px', height: '10px', borderRadius: '50%' },
  flagLabel: { flex: 1, fontSize: '13px' },
  flagStatus: { fontSize: '12px', color: 'rgba(255,255,255,0.5)' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '24px', cursor: 'pointer', lineHeight: 1 },
  modalBody: { display: 'flex', flexDirection: 'column', gap: '12px' },
  modalMetrics: { marginTop: '8px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' },
};

export default CommandCenter;